import React, { useState, useEffect } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Select,
} from "@chakra-ui/react";
import axiosInstance from "services/httpService";
import { subscriptionPlanRequest } from "services/request"; // Import the request constants

interface Plan {
  id: string;
  name: string;
  amountForCountry: number;
  currency: string;
  countryCurrency: string;
  duration: number;
}

export interface UpgradeUserPayload {
  planId: string;
  email: string;
  country: string;
  receiptReference: string;
}

interface UpgradeUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
  onUpgrade: (data: UpgradeUserPayload) => void;
}

const MAX_RAW_RECEIPT_LENGTH = 160;
const NORMALIZED_RECEIPT_PATTERN = /^[a-z0-9][a-z0-9 ._:/-]{2,119}$/;
const RECEIPT_ERROR =
  "Enter a valid receipt reference: 3–120 characters, starting with a letter or number, using only letters, numbers, spaces and . _ : / -";
const FALLBACK_UPGRADE_ERROR = "Failed to upgrade user. Please try again.";

// Mirrors the backend's normalizeManualReceipt so admins get feedback before
// the request is sent. The backend stays the source of truth.
export const isValidReceiptReference = (value: string): boolean => {
  if (value.length > MAX_RAW_RECEIPT_LENGTH || /[^\x20-\x7e]/.test(value)) return false;
  const normalized = value.trim().replace(/ +/g, " ").toLowerCase();
  return NORMALIZED_RECEIPT_PATTERN.test(normalized);
};

const getUpgradeErrorMessage = (error: unknown): string => {
  const apiError = (error as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  if (Array.isArray(apiError) && apiError.length > 0) return apiError.join(". ");
  if (typeof apiError === "string" && apiError.trim()) return apiError;
  return FALLBACK_UPGRADE_ERROR;
};

const UpgradeUserModal: React.FC<UpgradeUserModalProps> = ({
  isOpen,
  onClose,
  userEmail,
  onUpgrade,
}) => {
  const [planId, setPlanId] = useState<string>("");
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isUpgrading, setIsUpgrading] = useState<boolean>(false);
  const [country, setCountry] = useState<string>("usd");
  const [receiptReference, setReceiptReference] = useState<string>("");
  const [showValidation, setShowValidation] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>("");

  // Each opening is for a (possibly different) user and payment, so never
  // carry a previous receipt or error over.
  useEffect(() => {
    if (isOpen) {
      setPlanId("");
      setReceiptReference("");
      setShowValidation(false);
      setSubmitError("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && country) {
      void fetchPlans();
    }
  }, [isOpen, country]);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const requestUrl =
        country === "usd"
          ? subscriptionPlanRequest.SUBSCRIPTION_PLAN_USD
          : subscriptionPlanRequest.SUBSCRIPTION_PLAN;

      const response = await axiosInstance.get(requestUrl, {
        params: { country },
      });

      const plansData = response.data.data.map((plan: Plan) => {
        if (country === "usd") {
          return { ...plan, amountForCountry: plan.duration * 1, countryCurrency: "$" };
        } else if (country === "ngn") {
          return { ...plan, amountForCountry: plan.amountForCountry / 100, countryCurrency: "₦" };
        }
        return plan;
      });

      setPlans(plansData);
    } catch (error) {
      console.error("Failed to fetch plans:", error);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const isPlanInvalid = showValidation && !planId;
  const isReceiptInvalid = showValidation && !isValidReceiptReference(receiptReference);

  const handleSubmit = () => {
    setShowValidation(true);
    setSubmitError("");
    if (!planId || !isValidReceiptReference(receiptReference)) return;

    const data: UpgradeUserPayload = {
      planId,
      email: userEmail,
      country,
      receiptReference: receiptReference.trim(),
    };

    setIsUpgrading(true);

    axiosInstance
      .post(subscriptionPlanRequest.UPGRADE_USER, data)
      .then(() => {
        onUpgrade(data);
        onClose();
      })
      .catch((error: unknown) => {
        console.error("Failed to upgrade user plan:", error);
        setSubmitError(getUpgradeErrorMessage(error));
      })
      .finally(() => {
        setIsUpgrading(false);
      });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Upgrade User Subscription</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {submitError && (
            <Alert status="error" mb={6} borderRadius="md">
              <AlertIcon />
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}

          <FormControl>
            <FormLabel>Email</FormLabel>
            <Input type="email" value={userEmail} isReadOnly />
          </FormControl>

          <FormControl mt={10}>
            <FormLabel>Country</FormLabel>
            <Select
              value={country}
              onChange={(e) => {
                setCountry(e.target.value);
                setPlanId("");
              }}
              isDisabled={isUpgrading}
            >
              <option value="ngn">NGN</option>
              <option value="usd">USD</option>
            </Select>
          </FormControl>

          <FormControl mt={10} isInvalid={isPlanInvalid}>
            <FormLabel>Plan</FormLabel>
            <Select
              placeholder={loading ? "Loading plans..." : "Select plan"}
              value={planId}
              onChange={(e) => {
                setPlanId(e.target.value);
              }}
              isDisabled={loading || plans.length === 0 || isUpgrading}
            >
              {plans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {`${plan.name} - ${plan.countryCurrency}${plan.amountForCountry}  for ${plan.duration} month(s)`}
                </option>
              ))}
            </Select>
            <FormErrorMessage>Please select a plan.</FormErrorMessage>
          </FormControl>

          <FormControl mt={10} isRequired isInvalid={isReceiptInvalid}>
            <FormLabel>Payment receipt reference</FormLabel>
            <Input
              value={receiptReference}
              onChange={(e) => {
                setReceiptReference(e.target.value);
              }}
              placeholder="e.g. gtb/2026-11-04/0042"
              maxLength={MAX_RAW_RECEIPT_LENGTH}
              isDisabled={isUpgrading}
              autoComplete="off"
            />
            {isReceiptInvalid ? (
              <FormErrorMessage>{RECEIPT_ERROR}</FormErrorMessage>
            ) : (
              <FormHelperText>
                The bank transfer or receipt ID confirming this payment. Each receipt can be used
                once. If an upgrade fails, retry with the same reference.
              </FormHelperText>
            )}
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" onClick={onClose} isDisabled={isUpgrading}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            ml={3}
            isLoading={isUpgrading}
            isDisabled={loading || isUpgrading}
          >
            Upgrade
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UpgradeUserModal;
